import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrenciesStore } from '@app/ui/stores';
import { ReportType, type ReportEntity } from '@app/domain/entities';
import { transformReportAmounts } from '../report-viewer.utils';
import {
  loadExcelJs,
  sanitizeSheetName,
  sanitizeFilename,
  dateCellNumFmt,
  currencyNumFmt,
  addFormattedRow,
  setColumnWidths,
} from './report-export.utils';
import { REPORT_EXPORT_FILE_SERVICE, type ReportExportFileService } from './report-export-file.service';
import type { IReportExportUseCase } from './report-export.use-case';

interface GroupingDatesItem {
  date_part: number;
  amounts: Record<string, number>;
}

interface GroupingDatesReportData {
  options: {
    groupBy: 'day' | 'month' | 'year';
  };
  data: {
    incomes: GroupingDatesItem[];
    expenses: GroupingDatesItem[];
    incomesTotals: Record<string, number>;
    expensesTotals: Record<string, number>;
    totals: Record<string, number>;
  };
}

@Injectable({ providedIn: 'root' })
export class ExportGroupingByDatesUseCase implements IReportExportUseCase {
  readonly reportType = ReportType.GROUPING_BY_DATES;

  private readonly translate = inject(TranslateService);
  private readonly currenciesStore = inject(CurrenciesStore);
  private readonly reportExportFileService = inject<ReportExportFileService>(REPORT_EXPORT_FILE_SERVICE);

  async export(report: ReportEntity): Promise<void> {
    if (!report.data) return;

    const data = report.data as GroupingDatesReportData;
    const ExcelJS = await loadExcelJs();
    const workbook = new ExcelJS.Workbook();

    const currencies = this.currenciesStore.currencies();
    const { transformAmounts } = transformReportAmounts([...data.data.incomes, ...data.data.expenses], currencies);

    const allCurrencies = transformAmounts({}).map((a) => a.currency);
    const columnFormats: (string | undefined)[] = [
      dateCellNumFmt(data.options.groupBy),
      ...allCurrencies.map((c) => currencyNumFmt(c.code)),
    ];

    const sections = [
      {
        title: this.translate.instant('screens.reports.labels.expenses') as string,
        items: data.data.expenses,
        totals: transformAmounts(data.data.expensesTotals),
      },
      {
        title: this.translate.instant('screens.reports.labels.incomes') as string,
        items: data.data.incomes,
        totals: transformAmounts(data.data.incomesTotals),
      },
    ];

    for (const section of sections) {
      const sheet = workbook.addWorksheet(sanitizeSheetName(section.title));

      const dateHeader = this.translate.instant('common.labels.date') as string;
      sheet.addRow([dateHeader, ...allCurrencies.map((c) => c.name)]);
      setColumnWidths(sheet, ['date', ...allCurrencies.map(() => 'currency' as const)]);

      for (const item of section.items) {
        addFormattedRow(
          sheet,
          [new Date(item.date_part * 1000), ...transformAmounts(item.amounts).map((a) => a.value ?? 0)],
          columnFormats,
        );
      }

      if (section.totals.length > 0) {
        const totalLabel = this.translate.instant('screens.reports.labels.total') as string;
        // Totals row has no date cell — skip date format for that column
        addFormattedRow(
          sheet,
          [totalLabel, ...section.totals.map((a) => a.value ?? 0)],
          [undefined, ...allCurrencies.map((c) => currencyNumFmt(c.code))],
        );
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const typeName = this.translate.instant(`screens.reports.types.${report.type.toLowerCase()}`) as string;
    await this.reportExportFileService.saveExcelFile(
      buffer,
      `${sanitizeFilename(typeName)}-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }
}
